import React, { useCallback, useEffect, useState } from "react";
import { ITop10Article } from "../../../types/Top10Article";
import { useParams } from "react-router-dom";
import { getTop10Articles } from "../../Top10Articles/action";
import { Card, message, Pagination, Spin } from "antd";
import { getCounter } from "../../../lib/functions";
import {
  getMainWebsiteByID,
  setMainWebsiteHomepage,
  addTop10ArticleToCategory,
  removeTop10ArticleFromCategory,
} from "../actions";
import { IMainWebsite, IMainWebsiteCategory } from "../../../types/MainWebsite";

type Props = { fetchData: () => void };

const ManageTop10Articles = (props: Props) => {
  const { id, categoryId } = useParams();
  const [mainWebsite, setMainWebsite] = useState<IMainWebsite | null>(null);
  const [category, setCategory] = useState<IMainWebsiteCategory | null>(null);
  const [data, setData] = useState<ITop10Article[]>([]);
  const [page, setPage] = useState(1); // AntD Pagination is 1-based
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchCount = useCallback(async () => {
    try {
      const count = await getCounter("Top10Articles");
      setTotal(count || 0);
    } catch (error) {
      message.error("Failed to fetch count");
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const main_website = await getMainWebsiteByID(id!);
      if (main_website) {
        const cat = main_website.Categories.find((c) => c.id === categoryId);
        setMainWebsite(main_website);
        if (cat) {
          setCategory(cat);
        }
      }
      const articles = await getTop10Articles(pageSize, page - 1, query);
      setData(articles);
    } catch (error) {
      message.error("Failed to fetch articles");
    } finally {
      setLoading(false);
    }
  }, [id, categoryId, page, pageSize, query]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  const toggleArticleInCategory = async (articleId: string) => {
    if (!id || !categoryId) return;

    const isInCategory = category?.Top10Articles.some(
      (a) => a.id === articleId
    );

    try {
      if (isInCategory) {
        await removeTop10ArticleFromCategory(id, categoryId, articleId);
        message.success("Article removed from category");
      } else {
        // Add
        await addTop10ArticleToCategory(id, categoryId, articleId);
        message.success("Article added to category");
      }

      const updatedWebsite = await getMainWebsiteByID(id);
      setMainWebsite(updatedWebsite);
      const updatedCategory = updatedWebsite?.Categories.find(
        (c) => c.id === categoryId
      );
      if (updatedCategory) setCategory(updatedCategory);
      props.fetchData();
    } catch (error) {
      message.error("Failed to update category articles");
    }
  };

  return (
    <div className="space-y-6">
      <Spin spinning={loading}>
        <div className="grid grid-cols-3 gap-4">
          {data.map((dt) => {
            const isInCategory = category?.Top10Articles.some(
              (a) => a.id === dt.id
            );
            return (
              <Card
                key={dt.id}
                hoverable
                onClick={() => toggleArticleInCategory(dt.id)}
                style={{
                  border: isInCategory ? "2px solid #52c41a" : "",
                  backgroundColor: isInCategory ? "#f6ffed" : "",
                }}
              >
                <h2 className="text-lg font-bold">{dt.MainInfo.Title.En}</h2>
                <h3 className="text-xs">{dt.MainInfo.Title.Ar}</h3>
                <h3 className="text-xs">{dt.MainInfo.Title.Fr}</h3>
              </Card>
            );
          })}
        </div>
      </Spin>

      <div className="flex justify-end">
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          onChange={(p, ps) => {
            setPage(p);
            setPageSize(ps);
          }}
        />
      </div>
    </div>
  );
};

export default ManageTop10Articles;
